import React, { memo } from "react";
import { hexToRgba, kFormatter, METRIC } from "./calendarUtils";
import "./DateCell.scss";

/*
 * Mirrors the mobile DateCell mask. A day cannot hold more than 24h 59m, so
 * the parts are clamped rather than letting "9999" become "99:99".
 *
 * A lone digit above 2 cannot be the tens digit of a valid hour, so it is
 * padded and the colon inserted straight away — otherwise "930" would be read
 * as 93 hours. `isDeleting` keeps that colon from growing back while the user
 * backspaces through it.
 */
const MAX_HOURS = 24;
const MAX_MINUTES = 59;

const clamp = (part, max) => (Number(part) > max ? String(max) : part);

const formatTimeInput = (value, isDeleting) => {
  const digits = value.replace(/\D/g, "");

  if (digits.length === 0) return "";

  if (digits.length === 1) {
    if (!isDeleting && Number(digits) > 2) return `0${digits}:`;
    return digits;
  }

  if (digits.length === 2) {
    const hours = clamp(digits, MAX_HOURS);
    return isDeleting ? hours : `${hours}:`;
  }

  return `${clamp(digits.slice(0, 2), MAX_HOURS)}:${clamp(digits.slice(2, 4), MAX_MINUTES)}`;
};

// Digits with at most one decimal point and at most 2 decimal places — for
// shift-threshold contracts, where the cell takes a quantity like "1.5"
// instead of an HH:MM time.
const MAX_DECIMALS = 2;

const formatDecimalInput = (value) => {
  const cleaned = value.replace(/[^0-9.]/g, "");
  const firstDot = cleaned.indexOf(".");
  if (firstDot === -1) return cleaned;
  const decimals = cleaned.slice(firstDot + 1).replace(/\./g, "").slice(0, MAX_DECIMALS);
  return `${cleaned.slice(0, firstDot)}.${decimals}`;
};

const DateCell = memo(
  ({
    day,
    cellWidth,
    onPress,
    metricType,
    anyDateSelected,
    inputMode,
    inputValue,
    onInputChange,
    onInputFocus,
    metricColor,
    decimalInput = false,
  }) => {
    const handleCellClick = () => {
      if (!day.isDisabled && inputMode) {
        onPress(day.date);
      }
    };


    const handleInputChange = (e) => {
      if (decimalInput) {
        onInputChange(day.date, formatDecimalInput(e.target.value));
        return;
      }
      const isDeleting = e.target.value.length < (inputValue?.length ?? 0);
      onInputChange(day.date, formatTimeInput(e.target.value, isDeleting));
    };

    const handleInputFocus = () => {
      onInputFocus?.(day.date);
    };

    // Typing stays free-form ("1." must be enterable); once the user leaves
    // the field, a decimal quantity is shown as a fixed 2-decimal value.
    const handleInputBlur = () => {
      if (!decimalInput || !inputValue) return;
      const num = parseFloat(inputValue);
      if (Number.isNaN(num)) return;
      const fixed = num.toFixed(2);
      if (fixed !== inputValue) onInputChange(day.date, fixed);
    };


    // A day only reads as "booked" when its metric is non-zero — a genuine
    // zero (e.g. a shift logged with 0 hours, or 0 requests/shifts/amount)
    // gets neither the value text nor the tinted/dimmed treatment below.
    const hasMetric = day.hasPayRequest && day.metricValue !== null;
    const isZeroMetric = day.metricValue === "00:00" || day.metricValue === 0;
    const hasVisibleMetric = hasMetric && !isZeroMetric;
    const showInput = inputMode && day.isSelected;

    /*
     * Mirrors the mobile cell's background rules: once any date is picked, the
     * days already booked step back into grey so the picked ones stand out.
     * Selecting one of those grey days brings it forward as the accent fill
     * again, since `--selected` outranks `--dimmed`.
     *
     * This whole selected/dimmed treatment is specific to the date-picking
     * flow (AddPayRequestDialog, inputMode=true) — the read-only Work
     * Calendar (inputMode=false) still tracks clicked dates internally, but
     * shouldn't visibly bold or dim them.
     */
    const isDimmed = inputMode && anyDateSelected && hasVisibleMetric && !day.isSelected && !day.isDisabled;

    const classNames = [
      "date-cell",
      !day.isCurrentMonth && "date-cell--other-month",
      day.isDisabled && "date-cell--disabled",
      isDimmed && "date-cell--dimmed",
      inputMode && day.isSelected && "date-cell--selected",
      day.isToday && "date-cell--today",
      hasVisibleMetric && "date-cell--has-metric",
      inputMode && "date-cell--input-mode",
    ]
      .filter(Boolean)
      .join(" ");

    // Tints the cell fill with the currently selected metric's own colour
    // (blue for hours, pink for amount, etc.) instead of one fixed shade, so
    // the background tells you which metric is active the same way the
    // header badge and month strip already do.
    const cellStyle = {
      width: cellWidth,
      "--metric-color": hexToRgba(metricColor, 0.12),
    };

    return (
      <div
        className={classNames}
        style={cellStyle}
        onClick={handleCellClick}
        role="button"
        tabIndex={day.isDisabled ? -1 : 0}
        aria-selected={day.isSelected}
      >
        <span className="date-cell__day-number">{day.dayNumber}</span>

        {/*
         * The textbox replaces the booked-hours readout rather than stacking
         * under it — a selected day would otherwise show the same time twice,
         * once as static text and once in the input. Mirrors the mobile cell,
         * which is a single either/or.
         */}
        {showInput ? (
          <input
            type="text"
            className="date-cell__input"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onClick={(e) => e.stopPropagation()}
            //placeholder={decimalInput ? "0" : "HH:MM"}
            inputMode={decimalInput ? "decimal" : "numeric"}
            maxLength={decimalInput ? 6 : 5}
            style={{
              color: metricColor,
              //backgroundColor: hexToRgba(metricColor, 0.12)
            }}
          />
        ) : (
          hasVisibleMetric && (
            <span className="date-cell__metric-value" style={{ color: metricColor }}>
              {metricType?.value === METRIC.TOTAL_AMOUNT ? `$${kFormatter(day.metricValue)}` : day.metricValue}
            </span>
          )
        )}
      </div>
    );
  }
);

DateCell.displayName = "DateCell";

export default DateCell;
